import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import companyApiService from "../services/companyApiService";
import Container from "../../components/commonContainer/Container";
import BusinessIcon from "@mui/icons-material/Business";
import StoreIcon from "@mui/icons-material/Store";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { getMediaUrl } from "../../utils/mediaUrl";
import "./Dashboard.scss";

interface DashboardStats {
  overview: {
    totalCompanies: number;
    totalBranches: number;
    totalMarts: number;
    activeCompanies: number;
    inactiveCompanies: number;
  };
  recentCompanies: Array<{
    id: number;
    name: string;
    logo?: string;
    branchCount: number;
    createdAt: string;
  }>;
  companyBreakdown: Array<{
    id: number;
    name: string;
    logo?: string;
    branches: number;
    marts: number;
    isActive: boolean;
    maturity: {
      status: string;
      endDate: string;
      startDate: string;
      daysRemaining: number;
      planName: string;
      planAmount: number;
      planBillingType: string;
      currency: string;
      autoRenew: boolean;
      lastPaymentDate: string | null;
      paidUntilDate: string | null;
    } | null;
    weeklySales: Array<{
      saleDate: string;
      totalSales: number;
      salesCount: number;
    }>;
    salesAnalytics: {
      weeklyTotal: number;
      weeklyOrderCount: number;
      todayTotal: number;
      todayOrderCount: number;
      lastSaleDate: string | null;
      lastSaleAmount: number;
      lastSaleBillNumber: string | null;
      daysSinceLastSale: number | null;
      salesActivityStatus: "active" | "recent" | "slow" | "inactive";
    };
    createdAt: string;
  }>;
  weeklySales: Array<{
    companyId: number;
    saleDate: string;
    totalSales: number;
    salesCount: number;
  }>;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await companyApiService.getDashboardStats();
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string = "LKR") => {
    return `${currency} ${Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getSalesActivityBadge = (status: string) => {
    switch (status) {
      case "active":
        return {
          label: "Active Sales",
          color: "success",
          icon: <TrendingUpIcon fontSize="small" />,
        };
      case "recent":
        return {
          label: "Recent Sales",
          color: "info",
          icon: <AccessTimeIcon fontSize="small" />,
        };
      case "slow":
        return {
          label: "Slow Sales",
          color: "warning",
          icon: <TrendingDownIcon fontSize="small" />,
        };
      case "inactive":
        return {
          label: "No Sales",
          color: "danger",
          icon: <CancelIcon fontSize="small" />,
        };
      default:
        return { label: "Unknown", color: "secondary", icon: null };
    }
  };

  if (loading) {
    return (
      <Container className="dashboard">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="dashboard">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-btn">
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Company Dashboard</h1>
          <p className="subtitle">
            Overview of all your companies and operations
          </p>
        </div>
        <button
          className="add-company-btn"
          onClick={() => navigate("/company/shops")}
        >
          <AddIcon />
          Add New Company
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card companies">
          <div className="stat-icon">
            <BusinessIcon />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Companies</p>
            <h2 className="stat-value">
              {stats?.overview.totalCompanies || 0}
            </h2>
            <div className="stat-trend">
              <TrendingUpIcon />
              <span>Active: {stats?.overview.activeCompanies || 0}</span>
            </div>
          </div>
        </div>

        <div className="stat-card active">
          <div className="stat-icon">
            <CheckCircleIcon />
          </div>
          <div className="stat-content">
            <p className="stat-label">Active Companies</p>
            <h2 className="stat-value">
              {stats?.overview.activeCompanies || 0}
            </h2>
            <div className="stat-trend success">
              <CheckCircleIcon />
              <span>Operational</span>
            </div>
          </div>
        </div>

        <div className="stat-card inactive">
          <div className="stat-icon">
            <CancelIcon />
          </div>
          <div className="stat-content">
            <p className="stat-label">Inactive Companies</p>
            <h2 className="stat-value">
              {stats?.overview.inactiveCompanies || 0}
            </h2>
            <div className="stat-trend warning">
              <CancelIcon />
              <span>Pending</span>
            </div>
          </div>
        </div>

        <div className="stat-card branches">
          <div className="stat-icon">
            <AccountTreeIcon />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Branches</p>
            <h2 className="stat-value">{stats?.overview.totalBranches || 0}</h2>
            <div className="stat-trend">
              <TrendingUpIcon />
              <span>Operational</span>
            </div>
          </div>
        </div>

        <div className="stat-card marts">
          <div className="stat-icon">
            <StoreIcon />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Marts</p>
            <h2 className="stat-value">{stats?.overview.totalMarts || 0}</h2>
            <div className="stat-trend">
              <TrendingUpIcon />
              <span>Running</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content">
        {/* Company Breakdown */}
        <div className="dashboard-card company-breakdown full-width">
          <div className="card-header">
            <h3>Company Overview</h3>
          </div>
          <div className="card-content">
            {stats?.companyBreakdown && stats.companyBreakdown.length > 0 ? (
              <div className="breakdown-table">
                <table>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Status</th>
                      <th>Branches/Marts</th>
                      <th>Subscription Plan</th>
                      <th>Maturity</th>
                      <th>Sales Activity</th>
                      <th>Last Week Sales</th>
                      <th>Today's Sales</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.companyBreakdown.map((company) => {
                      const activityBadge = getSalesActivityBadge(
                        company.salesAnalytics.salesActivityStatus,
                      );
                      return (
                        <tr key={company.id}>
                          <td>
                            <div className="company-cell">
                              <div className="company-avatar-small">
                                {company.logo ? (
                                  <img
                                    src={getMediaUrl(company.logo)}
                                    alt={company.name}
                                  />
                                ) : (
                                  <BusinessIcon />
                                )}
                              </div>
                              <span>{company.name}</span>
                            </div>
                          </td>
                          <td>
                            {company.isActive ? (
                              <span className="status-badge active">
                                <CheckCircleIcon fontSize="small" />
                                Active
                              </span>
                            ) : (
                              <span className="status-badge inactive">
                                <CancelIcon fontSize="small" />
                                Inactive
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="count-group">
                              <span className="count-badge branches-badge">
                                {company.branches} Branches
                              </span>
                              <span className="count-badge marts-badge">
                                {company.marts} Marts
                              </span>
                            </div>
                          </td>
                          <td>
                            {company.maturity ? (
                              <div className="subscription-detail">
                                <div className="plan-info">
                                  <span className="plan-name">
                                    {company.maturity.planName}
                                  </span>
                                  <span className="plan-price">
                                    {formatCurrency(
                                      company.maturity.planAmount,
                                      company.maturity.currency,
                                    )}
                                    /{company.maturity.planBillingType}
                                  </span>
                                </div>
                                <span
                                  className={`sub-status ${company.maturity.status}`}
                                >
                                  {company.maturity.status}
                                </span>
                              </div>
                            ) : (
                              <span className="no-subscription">No Plan</span>
                            )}
                          </td>
                          <td>
                            {company.maturity ? (
                              <div className="maturity-detail">
                                <div className="maturity-days">
                                  <EventAvailableIcon fontSize="small" />
                                  <span
                                    className={
                                      company.maturity.daysRemaining < 7
                                        ? "days-critical"
                                        : company.maturity.daysRemaining < 30
                                          ? "days-warning"
                                          : "days-ok"
                                    }
                                  >
                                    {company.maturity.daysRemaining > 0
                                      ? `${company.maturity.daysRemaining} days left`
                                      : "Expired"}
                                  </span>
                                </div>
                                <span className="maturity-date">
                                  Ends: {formatDate(company.maturity.endDate)}
                                </span>
                                {company.maturity.autoRenew && (
                                  <span className="auto-renew-badge">
                                    Auto-Renew
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="no-maturity">-</span>
                            )}
                          </td>
                          <td>
                            <div className="sales-activity">
                              <span
                                className={`activity-badge ${activityBadge.color}`}
                              >
                                {activityBadge.icon}
                                {activityBadge.label}
                              </span>
                              {company.salesAnalytics.lastSaleDate && (
                                <div className="last-sale-info">
                                  <AccessTimeIcon fontSize="small" />
                                  <span>
                                    {company.salesAnalytics
                                      .daysSinceLastSale === 0
                                      ? "Today"
                                      : company.salesAnalytics
                                            .daysSinceLastSale === 1
                                        ? "Yesterday"
                                        : `${company.salesAnalytics.daysSinceLastSale} days ago`}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="weekly-sales-detail">
                              <div className="sales-amount">
                                <AttachMoneyIcon fontSize="small" />
                                <span className="amount-value">
                                  {formatCurrency(
                                    company.salesAnalytics.weeklyTotal,
                                    company.maturity?.currency || "LKR",
                                  )}
                                </span>
                              </div>
                              <div className="sales-count">
                                <ShoppingCartIcon fontSize="small" />
                                <span>
                                  {company.salesAnalytics.weeklyOrderCount}{" "}
                                  orders
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="today-sales-detail">
                              {company.salesAnalytics.todayOrderCount > 0 ? (
                                <>
                                  <div className="sales-amount today">
                                    <AttachMoneyIcon fontSize="small" />
                                    <span className="amount-value">
                                      {formatCurrency(
                                        company.salesAnalytics.todayTotal,
                                        company.maturity?.currency || "LKR",
                                      )}
                                    </span>
                                  </div>
                                  <div className="sales-count">
                                    <ShoppingCartIcon fontSize="small" />
                                    <span>
                                      {company.salesAnalytics.todayOrderCount}{" "}
                                      orders
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <span className="no-sales">No sales today</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              className="action-btn"
                              onClick={() =>
                                navigate(`/company/shops/${company.id}`)
                              }
                            >
                              <VisibilityIcon />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <AccountTreeIcon className="empty-icon" />
                <p>No data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button
            className="action-card"
            onClick={() => navigate("/company/shops")}
          >
            <BusinessIcon />
            <span>Manage Companies</span>
          </button>
          <button className="action-card" onClick={fetchDashboardData}>
            <TrendingUpIcon />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>
    </Container>
  );
};

export default Dashboard;
